import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { verifyMfaChallenge } from "@/lib/mfa.functions";
import { ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/_app/mfa-challenge")({
  component: MfaChallengePage,
});

function MfaChallengePage() {
  const { profile, role, signOut } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (profile && !profile.mfa_enabled) {
      sessionStorage.setItem("mfa_passed", "1");
      nav({ to: role === "admin" || role === "moderator" ? "/admin/overview" : "/dashboard" });
    }
  }, [profile, role, nav]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await verifyMfaChallenge({ data: { code } });
      sessionStorage.setItem("mfa_passed", "1");
      if (r.usedBypass) toast.success("Signed in with backup code");
      nav({ to: role === "admin" || role === "moderator" ? "/admin/overview" : "/dashboard" });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-bold">Two-factor verification</h1>
        </div>
        <p className="text-xs text-muted-foreground">
          Enter the 6-digit code from your authenticator app, or your personal backup code.
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Verification code</Label>
            <Input
              autoFocus
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              className="text-center font-mono text-lg tracking-[0.3em]"
            />
          </div>
          <Button type="submit" className="w-full" disabled={busy || code.length !== 6}>
            {busy ? "Verifying…" : "Verify"}
          </Button>
          <button
            type="button"
            onClick={async () => {
              sessionStorage.removeItem("mfa_passed");
              await signOut();
              nav({ to: "/login" });
            }}
            className="w-full text-center text-[11px] text-muted-foreground hover:text-foreground"
          >
            Cancel and sign out
          </button>
        </form>
      </div>
    </div>
  );
}
